import Link from "next/link";
import { CheckCircle2 } from "lucide-react";
import { BrokerAvatar } from "@/components/brokers/broker-avatar";

export type BrokerProfileCardProps = {
  href: string;
  id: string;
  name: string;
  image?: string | null;
  isVerified?: boolean;
  /** Brokerage name, or "Independent business broker". */
  organization: string;
  /** "City, ST". */
  locationLabel?: string | null;
  /** Industry specialty names, most relevant first. */
  specialties?: string[] | null;
  /** Falls back to a bio excerpt when there are no specialties. */
  bio?: string | null;
  yearsCount?: number | null;
  listingsCount?: number | null;
  ratingAverage?: number | null;
  ratingTotal?: number | null;
  /** Small label above the meta row, only when it adds information ("Featured"). */
  eyebrow?: string;
  /** Plain label; the card appends the arrow. */
  ctaLabel?: string;
};

function plural(count: number, one: string, many: string) {
  return count === 1 ? one : many;
}

/**
 * Directory / carousel card for a broker. Three fixed zones (identity,
 * context line, meta row) so every card has the same shape regardless of
 * how much data the broker has, and every fact appears exactly once.
 */
export function BrokerProfileCard({
  href,
  id,
  name,
  image,
  isVerified = true,
  organization,
  locationLabel,
  specialties,
  bio,
  yearsCount,
  listingsCount,
  ratingAverage,
  ratingTotal,
  eyebrow,
  ctaLabel = "View profile",
}: BrokerProfileCardProps) {
  const specialtyNames = (specialties ?? [])
    .map((s) => s?.trim())
    .filter((s): s is string => !!s)
    .slice(0, 3);
  const bioExcerpt = bio?.trim() ?? "";

  // Context line: what this broker works on. Always present so the card
  // never reads as empty.
  const context =
    specialtyNames.length > 0
      ? specialtyNames.join(" · ")
      : bioExcerpt.length > 0
        ? bioExcerpt
        : `Licensed business broker${locationLabel ? ` serving ${locationLabel}` : ""}`;

  // Meta facts: only non-zero values, rendered inline so height is stable.
  const facts: { key: string; value: string; label: string }[] = [];
  if (listingsCount != null && listingsCount > 0) {
    facts.push({
      key: "listings",
      value: String(listingsCount),
      label: plural(listingsCount, "listing", "listings"),
    });
  }
  if (yearsCount != null && yearsCount > 0) {
    facts.push({
      key: "years",
      value: String(yearsCount),
      label: plural(yearsCount, "yr", "yrs"),
    });
  }
  if (ratingTotal && ratingAverage != null && ratingAverage > 0) {
    facts.push({
      key: "rating",
      value: `★ ${ratingAverage.toFixed(1)}`,
      label: `(${ratingTotal})`,
    });
  }

  return (
    <Link href={href} className="group block h-full min-w-0 w-full">
      <article className="flex h-full min-w-0 w-full flex-col rounded-2xl border border-border bg-card p-5 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:border-border group-hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
        {/* Zone 1: identity */}
        <div className="flex items-start gap-3.5">
          <BrokerAvatar
            id={id}
            name={name}
            image={image}
            isVerified={isVerified}
            showVerified={false}
            size="lg"
          />
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex items-center gap-1.5">
              <h3 className="truncate text-base font-semibold text-foreground">
                {name}
              </h3>
              {isVerified && (
                <span
                  role="img"
                  title="License verified by Venturu"
                  aria-label="License verified by Venturu"
                  className="inline-flex shrink-0"
                >
                  <CheckCircle2 className="size-4 text-[#00A83B]" aria-hidden />
                </span>
              )}
            </div>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              {organization}
            </p>
            {locationLabel && (
              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                {locationLabel}
              </p>
            )}
          </div>
        </div>

        {/* Zone 2: context line */}
        <p className="mt-4 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
          {context}
        </p>

        {/* Zone 3: meta row */}
        <div className="mt-auto pt-5">
          {eyebrow && (
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              {eyebrow}
            </p>
          )}
          <div className="flex items-center justify-between gap-3 border-t border-border pt-3.5 text-sm">
            {facts.length > 0 ? (
              <dl className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-muted-foreground">
                {facts.map((fact, index) => (
                  <div
                    key={fact.key}
                    className="flex items-baseline gap-1 whitespace-nowrap"
                  >
                    {index > 0 && <span aria-hidden>·</span>}
                    <dd className="font-data font-semibold tabular-nums text-foreground">
                      {fact.value}
                    </dd>
                    <dt>{fact.label}</dt>
                  </div>
                ))}
              </dl>
            ) : (
              <span className="text-muted-foreground">Free to contact</span>
            )}
            <span className="shrink-0 font-medium text-foreground transition-transform duration-200 group-hover:translate-x-0.5">
              {ctaLabel} →
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}

BrokerProfileCard.displayName = "BrokerProfileCard";
